// ============================================================
// ANSICHTSSACHE-AGENT – Steuerpanel
// ============================================================
// EIN Bauteil fuer alle kleinen Flaechen, die an einem Knopf
// haengen: Zeiterfassung, Kundenfreigabe, Benachrichtigungen,
// Suchergebnisse, Auswahllisten, Menues.
//
// Vorher war jede dieser Flaechen von Hand gebaut — sieben Mal
// dieselbe Absicht mit sieben leicht verschiedenen Abstaenden,
// Breiten und Verhaltensweisen. Das faellt einzeln nicht auf und
// in der Summe sofort: mal schliesst Escape, mal nicht; mal
// liegt der Rand bei 12px, mal bei 16px.
//
// Das Verhalten kommt von Radix und ist damit ueberall gleich:
// Escape schliesst, ein Klick daneben schliesst, der Fokus geht
// beim Schliessen dorthin zurueck, wo er herkam, und das Panel
// weicht dem Bildschirmrand aus, statt darueber hinauszulaufen.
// ============================================================

"use client";

import { useEffect, useId, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { DateInput, type DateInputProps } from "./date-input";

/** Drei Breiten, mehr braucht es nicht. */
const BREITEN = {
  schmal: "w-56",
  mittel: "w-72",
  breit: "w-[380px]",
  /** So breit wie der Inhalt — eine Leiste aus Gesichtern. */
  passend: "w-max",
} as const;

/** Wie lange der Zeiger daneben sein darf, bevor das Panel zugeht. */
const NACHLAUF_MS = 160;

export function Steuerpanel({
  ausloeser,
  offen,
  onOffenAendern,
  titel,
  unterzeile,
  breite = "mittel",
  ausrichtung = "end",
  abstand = 8,
  mitSchliessen = false,
  beimUeberfahren = false,
  fuss,
  className,
  children,
}: {
  ausloeser: React.ReactNode;
  /** Optional von aussen gesteuert; sonst regelt das Panel sich selbst. */
  offen?: boolean;
  onOffenAendern?: (offen: boolean) => void;
  titel?: string;
  unterzeile?: string;
  breite?: keyof typeof BREITEN;
  ausrichtung?: "start" | "center" | "end";
  /**
   * Luft zwischen Ausloeser und Panel in Pixeln.
   *
   * Acht sind der Normalfall — ein Panel, das an einem Knopf in
   * einer Schiene haengt, braucht Luft, sonst klebt es an der
   * Fassung. Haengt es an einem einzelnen Knopf in einem Band,
   * liest sich dieselbe Luft als „faellt herunter": es fehlt die
   * Fassung, gegen die man sie liest. Dort weniger nehmen.
   */
  abstand?: number;
  /** Kreuz oben rechts — fuer Panels, die man laenger offen haelt. */
  mitSchliessen?: boolean;
  /**
   * Oeffnet schon, wenn die Maus darueber faehrt, und geht zu,
   * sobald sie Knopf und Panel verlassen hat — fuer kleine
   * Leisten, die man im Vorbeigehen bedient (Personenfilter).
   *
   * Nur fuer die Maus: auf dem Finger und mit der Tastatur bleibt
   * es ein Klick-Panel, sonst kaeme man nie hinein. Beim Oeffnen
   * per Zeiger bleibt der Fokus, wo er war — ein Panel, das beim
   * Vorbeifahren den Fokus nimmt, zeigt einen Fokusring, um den
   * niemand gebeten hat.
   */
  beimUeberfahren?: boolean;
  fuss?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  const [eigenOffen, setEigenOffen] = useState(false);
  const perZeiger = useRef(false);
  const zuPerZeiger = useRef(false);
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (uhr.current) clearTimeout(uhr.current);
    },
    [],
  );

  const istOffen = offen ?? eigenOffen;

  function setzeOffen(neu: boolean) {
    if (!neu) perZeiger.current = false;
    if (offen === undefined) setEigenOffen(neu);
    onOffenAendern?.(neu);
  }

  function zeigerRein(e: React.PointerEvent) {
    if (!beimUeberfahren || e.pointerType !== "mouse") return;
    if (uhr.current) clearTimeout(uhr.current);
    if (!istOffen) {
      perZeiger.current = true;
      setzeOffen(true);
    }
  }

  function zeigerRaus(e: React.PointerEvent) {
    if (!beimUeberfahren || e.pointerType !== "mouse" || !perZeiger.current) return;
    if (uhr.current) clearTimeout(uhr.current);
    uhr.current = setTimeout(() => {
      zuPerZeiger.current = true;
      setzeOffen(false);
    }, NACHLAUF_MS);
  }

  return (
    <Popover.Root
      open={beimUeberfahren ? istOffen : offen}
      onOpenChange={beimUeberfahren ? setzeOffen : onOffenAendern}
    >
      <Popover.Trigger
        asChild
        onPointerEnter={zeigerRein}
        onPointerLeave={zeigerRaus}
        onClick={(e) => {
          // Per Zeiger schon offen: der Klick darauf schliesst es
          // nicht, er HAELT es — dann bleibt es auch stehen, wenn
          // die Maus weiterzieht, bis man daneben klickt. Radix
          // ueberspringt seinen Umschalter, wenn das Ereignis schon
          // verbraucht ist.
          if (beimUeberfahren && perZeiger.current && istOffen) {
            e.preventDefault();
            perZeiger.current = false;
            if (uhr.current) clearTimeout(uhr.current);
          }
        }}
      >
        {ausloeser}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          onPointerEnter={zeigerRein}
          onPointerLeave={zeigerRaus}
          onOpenAutoFocus={(e) => {
            if (perZeiger.current) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            if (zuPerZeiger.current) e.preventDefault();
            zuPerZeiger.current = false;
          }}
          align={ausrichtung}
          sideOffset={abstand}
          collisionPadding={12}
          className={cn(
            "glass-strong border-border shadow-e4 z-50 rounded-xl border",
            // Nie hoeher als der Bildschirm — sonst laeuft eine
            // lange Liste unten heraus und ist nicht erreichbar.
            // Und nie hoeher als der Platz auf der Seite, zu der es
            // aufklappt: ein Knopf in der Bildmitte (Zeitachse im
            // Banking) klappt bei wenig Hoehe nach oben, und ohne
            // diese Grenze stiesse das Panel dort an den Rand.
            "max-h-[min(70vh,600px,var(--radix-popover-content-available-height,600px))] overflow-y-auto",
            BREITEN[breite],
            className,
          )}
        >
          {(titel || mitSchliessen) && (
            <div className="flex items-start justify-between gap-3 px-3.5 pt-3.5 pb-1">
              <div className="min-w-0">
                {titel && <p className="text-overline text-text-tertiary">{titel}</p>}
                {unterzeile && (
                  <p className="text-body-md text-text-primary truncate font-medium">
                    {unterzeile}
                  </p>
                )}
              </div>
              {mitSchliessen && (
                <Popover.Close asChild>
                  <button
                    type="button"
                    aria-label="Schließen"
                    className="text-text-tertiary duration-fast hover:bg-bg-secondary hover:text-text-primary grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </Popover.Close>
              )}
            </div>
          )}

          <div className="p-1.5">{children}</div>

          {fuss && (
            <div className="border-border-subtle text-caption text-text-tertiary border-t px-3.5 py-2.5">
              {fuss}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Eine Zeile im Panel. Immer dieselbe Hoehe, derselbe Rand,
 * dasselbe Aufleuchten — egal ob darin eine Person, eine
 * Benachrichtigung oder ein Menuepunkt steht.
 */
export function PanelZeile({
  als: Als = "button",
  aktiv,
  ton = "normal",
  className,
  children,
  ...rest
}: {
  als?: "button" | "div";
  aktiv?: boolean;
  ton?: "normal" | "gefahr";
  className?: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <Als
      {...(Als === "button" ? { type: "button" as const } : {})}
      className={cn(
        "text-body-sm flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left",
        "duration-fast ease-standard transition-colors",
        ton === "gefahr"
          ? "text-danger hover:bg-danger-subtle"
          : aktiv
            ? "bg-bg-tertiary text-text-primary font-medium"
            : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary",
        className,
      )}
      {...rest}
    >
      {children}
    </Als>
  );
}

/** Ueberschrift innerhalb eines Panels, wenn es Abschnitte gibt. */
export function PanelAbschnitt({ children }: { children: React.ReactNode }) {
  return <p className="text-caption text-text-tertiary px-2 pt-2.5 pb-1">{children}</p>;
}

/** Trennlinie zwischen Abschnitten. */
export function PanelTrenner() {
  return <div className="bg-border-subtle my-1.5 h-px" aria-hidden />;
}

/**
 * Beschriftete Felder im Panel — „Springe zu“, „Von“, „Bis“.
 *
 * Die Beschriftung steht links an der Textkante der Zeilen, das
 * Feld rechts daneben: eine Zeile je Feld statt Ueberschrift und
 * Feld untereinander. So bleibt ein Panel mit Datumswahl niedrig
 * genug, dass es auch an einem Knopf in der Bildmitte (Zeitachse
 * im Banking) auf einen Laptop-Bildschirm passt.
 */
export function PanelFields({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1 px-2 pt-1 pb-1">
      {children}
    </div>
  );
}

/**
 * Ein Datumsfeld mit Beschriftung — steht in `PanelFields`.
 *
 * SOLANGE JEMAND DARIN TIPPT, GILT, WAS IM FELD STEHT, nicht der
 * Wert von aussen. Ein Datumsfeld meldet schon nach dem Monat ein
 * ganzes Datum; die Panels springen daraufhin sofort (Zeitachse,
 * Tagwahl der Zeiterfassung). Setzt die Seite den Wert dann neu —
 * und sei es auf denselben Tag oder den Monatsersten —, stellt der
 * Browser die Schreibmarke zurueck auf das erste Segment, und Tag
 * und Jahr landen im Monat. Beim Verlassen zeigt das Feld wieder,
 * was gilt.
 */
export function PanelDateField({
  label,
  value,
  onChange,
  onBlur,
  className,
  ...props
}: { label: string; value: string } & Omit<DateInputProps, "value">) {
  const id = useId();
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <>
      <label htmlFor={props.id ?? id} className="text-caption text-text-tertiary">
        {label}
      </label>
      <DateInput
        id={id}
        {...props}
        value={typed ?? value}
        onChange={(e) => {
          setTyped(e.target.value);
          onChange?.(e);
        }}
        onBlur={(e) => {
          setTyped(null);
          onBlur?.(e);
        }}
        className={cn(
          "border-border bg-bg-primary text-body-sm text-text-primary duration-fast hover:border-border-strong focus:border-accent/40 focus:ring-accent/30 h-9 w-full rounded-lg border px-2 transition-colors focus:ring-2 focus:outline-none",
          className,
        )}
      />
    </>
  );
}
